import { useCallback, useMemo, useState } from "react";
import type {
    BoardColumnDef,
    BoardLaneDef,
    BoardPlacement,
} from "@design-system/Board/Board.types";
import { createIsPlacementShown } from "@design-system/Board/boardVisibility";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import type { DemoTask } from "./fixtures/demoTasks";
import type { TaskBoardChange } from "./taskBoardActions";
import {
    describeTaskUpdate,
    type TaskChangeOutcome,
} from "./taskBoardChangeRules";

interface ShownTasks {
    /** Tasks past the search and status filters. */
    visibleTasks: readonly DemoTask[];
    getPlacements: (task: DemoTask) => readonly BoardPlacement[];
    /** The board's columns and lanes, as it gets them. */
    columns: readonly BoardColumnDef[];
    lanes?: readonly BoardLaneDef[];
    collapsedColumnIds: readonly string[];
    collapsedLaneIds: readonly string[];
}

interface UseTaskBoardSelectionArgs extends ShownTasks {
    updateTasks: (
        taskIds: ReadonlySet<string>,
        change: TaskBoardChange,
    ) => TaskChangeOutcome;
}

const NO_TASK_IDS: ReadonlySet<string> = new Set();

/** Tasks with a card on screen: filtered in, at a placement the board shows. */
const getShownTaskIds = ({
    visibleTasks,
    getPlacements,
    ...visibility
}: ShownTasks) => {
    const isPlacementShown = createIsPlacementShown(visibility);
    return new Set(
        visibleTasks
            .filter((task) => getPlacements(task).some(isPlacementShown))
            .map((task) => task.id),
    );
};

const keepShown = (
    taskIds: ReadonlySet<string>,
    shownTaskIds: ReadonlySet<string>,
) =>
    [...taskIds].every((taskId) => shownTaskIds.has(taskId))
        ? taskIds
        : new Set([...taskIds].filter((taskId) => shownTaskIds.has(taskId)));

/**
 * The playground's selection. Tasks that leave the screen (filtered out,
 * regrouped into a collapsed column or lane, collapsed away) drop out of it,
 * so a bulk action only ever reaches cards the user can see.
 * `applyToSelection` runs one change from the action set on every selected
 * task and toasts what it did; the selection stays for the next action.
 */
export const useTaskBoardSelection = ({
    visibleTasks,
    getPlacements,
    columns,
    lanes,
    collapsedColumnIds,
    collapsedLaneIds,
    updateTasks,
}: UseTaskBoardSelectionArgs) => {
    const shownTaskIds = useMemo(
        () =>
            getShownTaskIds({
                visibleTasks,
                getPlacements,
                columns,
                lanes,
                collapsedColumnIds,
                collapsedLaneIds,
            }),
        [
            visibleTasks,
            getPlacements,
            columns,
            lanes,
            collapsedColumnIds,
            collapsedLaneIds,
        ],
    );
    const [selectedTaskIds, setSelectedTaskIds] =
        useState<ReadonlySet<string>>(NO_TASK_IDS);
    const shownSelectedIds = keepShown(selectedTaskIds, shownTaskIds);
    if (shownSelectedIds !== selectedTaskIds) {
        setSelectedTaskIds(shownSelectedIds);
    }

    const { toast } = useToaster();
    const applyToSelection = useCallback(
        (change: TaskBoardChange) => {
            toast(
                describeTaskUpdate(
                    change,
                    updateTasks(shownSelectedIds, change),
                ),
            );
        },
        [updateTasks, shownSelectedIds, toast],
    );
    const clearSelection = useCallback(
        () => setSelectedTaskIds(NO_TASK_IDS),
        [],
    );

    return {
        selectedTaskIds: shownSelectedIds,
        setSelectedTaskIds,
        clearSelection,
        applyToSelection,
    };
};
